import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createProfileIndieOption,
  deleteProfileIndieOption,
  getProfileIndieOption,
  listProfileIndieOptions,
  updateProfileIndieOption,
  type CreateProfileIndieOptionBody,
  type ListProfileIndieOptionsParams,
  type PaginatedProfileIndieOptions,
  type ProfileIndieOption,
  type UpdateProfileIndieOptionBody,
} from "../api/profileIndieOptions";

export function useProfileIndieOptions(params: ListProfileIndieOptionsParams) {
  return useQuery<PaginatedProfileIndieOptions>({
    queryKey: ["profile-indie-options", "list", params],
    queryFn: () => listProfileIndieOptions(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useProfileIndieOption(optionName: string | null, regionId = 1) {
  return useQuery<ProfileIndieOption>({
    queryKey: ["profile-indie-options", "detail", regionId, optionName],
    queryFn: () => getProfileIndieOption(optionName as string, regionId),
    enabled: !!optionName,
    staleTime: 30_000,
  });
}

export function useCreateProfileIndieOption() {
  const queryClient = useQueryClient();
  return useMutation<ProfileIndieOption, Error, CreateProfileIndieOptionBody>({
    mutationFn: (body) => createProfileIndieOption(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile-indie-options"] });
    },
  });
}

export function useUpdateProfileIndieOption() {
  const queryClient = useQueryClient();
  return useMutation<
    ProfileIndieOption,
    Error,
    { optionName: string; body: UpdateProfileIndieOptionBody }
  >({
    mutationFn: ({ optionName, body }) => updateProfileIndieOption(optionName, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile-indie-options"] });
    },
  });
}

export function useDeleteProfileIndieOption() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { optionName: string; regionId?: number }>({
    mutationFn: ({ optionName, regionId }) => deleteProfileIndieOption(optionName, regionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile-indie-options"] });
    },
  });
}
